import React, { useState, useEffect, useMemo, useCallback } from "react";
import DailySalesReport from "./DailySalesReport";
import apiClient from "../api/ResturantClient";
import LoadingSpinner from "./LoadingSpinner";
import {
  subscribeToOrderUpdates,
  subscribeToNewOrder,
  disconnectSocket,
} from "./utils/socket";

const OrderSummary = () => {
  const [orders, setOrders] = useState([]);
  const [selectedOrders, setSelectedOrders] = useState([]);
  const [stats, setStats] = useState({ totalOrders: 0, paidOrders: 0 });
  const [sortOrder, setSortOrder] = useState("desc");
  const [filterOption, setFilterOption] = useState("Pending");
  const restaurant_id = sessionStorage.getItem("restaurant_id");
  const [expandedRows, setExpandedRows] = useState(new Set());
  const [currentPage, setCurrentPage] = useState(1);
  const [ordersPerPage] = useState(10);
  const [searchQuery, setSearchQuery] = useState("");
  const [processingOrders, setProcessingOrders] = useState(new Set());
  const [isProcessingBulk, setIsProcessingBulk] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [totalPages, setTotalPages] = useState(1);
  const [selectedOrdersMap, setSelectedOrdersMap] = useState({});

  const handleMarkSelectedPaid = async () => {
    if (isProcessingBulk) return;

    setIsProcessingBulk(true);
    try {
      await Promise.all(
        Object.keys(selectedOrdersMap).map((orderId) =>
          handlePaymentChange(parseInt(orderId), "Completed")
        )
      );
      setSelectedOrdersMap({});
    } finally {
      setIsProcessingBulk(false);
    }
  };

  const fetchOrders = useCallback(
    async (page = 1, perPage = ordersPerPage) => {
      try {
        if (!restaurant_id) {
          setOrders([]);
          return;
        }
        const url = `/customer/orders/${restaurant_id}?page=${page}&per_page=${perPage}&filter=${filterOption}`;

        const response = await apiClient.get(url);
        const { orders: fetchedOrders, pagination } = response.data;

        setOrders(fetchedOrders);
        setTotalPages(pagination.total_pages);

        setStats({
          totalOrders: pagination.working_hours_total_orders,
          paidOrders: pagination.working_hours_paid_orders,
        });
      } catch (error) {
        console.log("Error details:", error.response);
        setOrders([]);
      } finally {
        setIsLoading(false);
      }
    },
    [restaurant_id, filterOption, ordersPerPage]
  );

  useEffect(() => {
    fetchOrders(currentPage);
  }, [
    restaurant_id,
    currentPage,
    sortOrder,
    filterOption,
    searchQuery,
    fetchOrders,
  ]);

  // NEW: Socket.IO useEffect hook for real-time updates
  useEffect(() => {
    if (!restaurant_id) return;

    const handleOrderUpdate = (orderData) => {
      try {
        orderData = JSON.parse(orderData);
        console.log("Order update received in OrderSummary:", orderData);

        setOrders((prevOrders) => {
          // Check if order already exists
          const existingOrderIndex = prevOrders.findIndex(
            (order) => order.order_id === orderData.order_id
          );

          if (existingOrderIndex !== -1) {
            // Update existing order
            const updatedOrders = [...prevOrders];
            updatedOrders[existingOrderIndex] = {
              ...updatedOrders[existingOrderIndex],
              status: orderData.status,
              total_amount:
                orderData.total_amount ||
                updatedOrders[existingOrderIndex].total_amount,
              // Preserve other existing properties
            };
            return updatedOrders;
          }
          return prevOrders; // If order doesn't exist, do nothing
        });
      } catch (error) {
        console.error("Error processing order update:", error);
      }
    };

    const handleNewOrder = (orderData) => {
      try {
        orderData = JSON.parse(orderData);
        console.log("New order received in OrderSummary:", orderData);

        setOrders((prevOrders) => {
          // Check if order already exists to avoid duplicates
          const orderExists = prevOrders.some(
            (order) => order.order_id === orderData.order_id
          );

          if (!orderExists) {
            // Add new order at the beginning of the list
            return [orderData, ...prevOrders];
          }
          return prevOrders;
        });

        // Refetch to update stats and pagination
        fetchOrders(currentPage);
      } catch (error) {
        console.error("Error processing new order:", error);
      }
    };

    // Subscribe to Socket.IO events
    const unsubscribeOrderUpdates = subscribeToOrderUpdates(
      restaurant_id,
      handleOrderUpdate
    );
    const unsubscribeNewOrder = subscribeToNewOrder(
      restaurant_id,
      handleNewOrder
    );

    // Cleanup function
    return () => {
      unsubscribeOrderUpdates();
      unsubscribeNewOrder();
    };
  }, [restaurant_id, currentPage, fetchOrders]);

  const toggleRowExpand = (orderId) => {
    setExpandedRows((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(orderId)) {
        newSet.delete(orderId);
      } else {
        newSet.add(orderId);
      }
      return newSet;
    });
  };

  const handleSortChange = (e) => {
    setSortOrder(e.target.value);
  };

  const handleFilterChange = (e) => {
    setFilterOption(e.target.value);
    setCurrentPage(1);
  };

  const handlePaymentChange = async (orderId, status) => {
    if (processingOrders.has(orderId)) return;

    setProcessingOrders((prev) => new Set(prev).add(orderId));
    try {
      await apiClient.post(
        `/kitchen/restaurant/${restaurant_id}/update-order-status/${orderId}`,
        { status }
      );
      setOrders((orders) =>
        orders.map((order) =>
          order.order_id === orderId ? { ...order, status } : order
        )
      );
    } catch (error) {
      alert(
        `Failed to update order status to ${status}. Please try again later.`
      );
    } finally {
      setProcessingOrders((prev) => {
        const newSet = new Set(prev);
        newSet.delete(orderId);
        return newSet;
      });
    }
  };

  const handleSelectOrder = (order) => {
    setSelectedOrdersMap((prevMap) => {
      const newMap = { ...prevMap };
      if (newMap[order.order_id]) {
        // If order is already selected, remove it
        delete newMap[order.order_id];
      } else {
        // If order is not selected, add it with its details
        newMap[order.order_id] = {
          order_id: order.order_id,
          total_amount: order.total_amount,
          status: order.status,
        };
      }
      return newMap;
    });
  };

  const filteredOrders = useMemo(() => {
    return orders.filter((order) => {
      const matchesSearch =
        searchQuery === "" ||
        order.customer_name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        order.order_id.toString().includes(searchQuery);
      if (filterOption === "all") {
        return matchesSearch;
      }
      return matchesSearch;
    });
  }, [orders, searchQuery, filterOption]);

  const totalSelectedAmount = Object.values(selectedOrdersMap).reduce(
    (total, order) => total + order.total_amount,
    0
  );

  const Pagination = () => {
    return (
      <div className="flex justify-center items-center mt-6 gap-3">
        <button
          onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
          disabled={currentPage === 1}
          className="px-4 py-2 rounded-lg bg-gradient-to-r from-blue-500 to-purple-500 text-white disabled:bg-gray-300 disabled:from-gray-300 disabled:to-gray-400 shadow-md hover:shadow-lg transition-all transform hover:scale-105 disabled:hover:scale-100"
        >
          Previous
        </button>
        <span className="mx-4 text-gray-700 font-medium">
          Page {currentPage} of {totalPages}
        </span>
        <button
          onClick={() =>
            setCurrentPage((prev) => Math.min(prev + 1, totalPages))
          }
          disabled={currentPage === totalPages}
          className="px-4 py-2 rounded-lg bg-gradient-to-r from-blue-500 to-purple-500 text-white disabled:bg-gray-300 disabled:from-gray-300 disabled:to-gray-400 shadow-md hover:shadow-lg transition-all transform hover:scale-105 disabled:hover:scale-100"
        >
          Next
        </button>
      </div>
    );
  };

  if (isLoading) {
    return <LoadingSpinner />;
  }

  return (
    <div className="w-full px-4 md:max-w-6xl mx-auto p-2 md:p-6 bg-white/80 backdrop-blur-sm rounded-2xl shadow-lg border border-gray-200/60">
      <h2 className="text-2xl md:text-3xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent text-center mb-4 md:mb-6">
        Order Summary
      </h2>

      <div className="flex flex-col md:flex-row gap-4 mb-4 md:mb-6">
        <div className="w-full md:w-1/2">
          <div className="relative">
            <input
              type="text"
              placeholder="Search by customer name or order ID..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full p-3 pl-10 text-sm md:text-base border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white/50 backdrop-blur-sm shadow-sm"
            />
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <svg className="h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
            </div>
          </div>
        </div>
        <div className="w-full md:w-1/2">
          <DailySalesReport restaurant_id={restaurant_id} />
        </div>
      </div>

      <div className="flex flex-col md:flex-row gap-4 mb-6">
        <div className="w-full md:flex-1">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Filter by:
          </label>
          <select
            className="w-full p-3 text-sm md:text-base border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white/50 backdrop-blur-sm shadow-sm"
            value={filterOption}
            onChange={handleFilterChange}
          >
            <option value="Pending">Pending Orders</option>
            <option value="Completed">Completed Orders</option>
            <option value="Cancelled">Cancelled Orders</option>
            <option value="all">All Orders</option>
          </select>
        </div>

        <div className="w-full md:flex-1">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Sort by:
          </label>
          <select
            className="w-full p-3 text-sm md:text-base border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white/50 backdrop-blur-sm shadow-sm"
            value={sortOrder}
            onChange={handleSortChange}
          >
            <option value="desc">Newest First</option>
            <option value="asc">Oldest First</option>
          </select>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6">
        <div className="bg-gradient-to-br from-blue-50 to-blue-100/50 p-4 rounded-xl shadow-md border border-blue-200/50">
          <h3 className="text-sm md:text-base font-semibold text-gray-700 mb-2">
            Total Orders
          </h3>
          <p className="text-2xl md:text-3xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
            {stats.totalOrders}
          </p>
        </div>
        <div className="bg-gradient-to-br from-green-50 to-green-100/50 p-4 rounded-xl shadow-md border border-green-200/50">
          <h3 className="text-sm md:text-base font-semibold text-gray-700 mb-2">
            Paid Orders
          </h3>
          <p className="text-2xl md:text-3xl font-bold bg-gradient-to-r from-green-600 to-blue-600 bg-clip-text text-transparent">
            {stats.paidOrders}
          </p>
        </div>
      </div>

      <div className="overflow-x-auto -mx-4 md:mx-0 rounded-xl border border-gray-200/60 shadow-sm">
        <div className="inline-block min-w-full align-middle">
          <table className="min-w-full">
            <thead className="bg-gradient-to-r from-gray-50 to-blue-50/30">
              <tr>
                <th className="p-3 md:p-4 text-left text-xs md:text-sm font-semibold text-gray-600">
                  Select
                </th>
                <th className="p-3 md:p-4 text-left text-xs md:text-sm font-semibold text-gray-600">
                  Table
                </th>
                <th className="p-3 md:p-4 text-left text-xs md:text-sm font-semibold text-gray-600">
                  Order Details
                </th>
                <th className="p-3 md:p-4 text-left text-xs md:text-sm font-semibold text-gray-600">
                  Customer
                </th>
                <th className="p-3 md:p-4 text-left text-xs md:text-sm font-semibold text-gray-600">
                  Total
                </th>
                <th className="p-3 md:p-4 text-left text-xs md:text-sm font-semibold text-gray-600">
                  Status
                </th>
                <th className="p-3 md:p-4 text-left text-xs md:text-sm font-semibold text-gray-600">
                  Action
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200/60">
              {filteredOrders.map((order) => (
                <tr key={order.order_id} className="hover:bg-gray-50/50 transition-colors">
                  <td className="p-3 md:p-4 text-xs md:text-sm">
                    {order.status !== "Completed" &&
                      order.status !== "Pending" &&
                      order.status !== "Approved" &&
                      order.status !== "Cancelled" && (
                        <input
                          type="checkbox"
                          checked={!!selectedOrdersMap[order.order_id]}
                          onChange={() => handleSelectOrder(order)}
                          disabled={processingOrders.has(order.order_id)}
                          className={`rounded border-gray-300 text-blue-600 focus:ring-blue-500 transition-all ${
                            processingOrders.has(order.order_id)
                              ? "opacity-50 cursor-not-allowed"
                              : "hover:border-blue-400"
                          }`}
                        />
                      )}
                  </td>
                  <td className="p-3 md:p-4 text-xs md:text-sm font-medium text-gray-900">
                    {order.table_number ? order.table_number : "Pick-up"}
                  </td>
                  <td className="p-3 md:p-4">
                    <div className="flex flex-col">
                      <div className="flex items-center gap-2">
                        <span className="font-semibold text-gray-900">
                          #{order.order_id}
                        </span>
                        <button
                          className="md:hidden rounded-full p-1 hover:bg-gray-100 transition-colors"
                          onClick={() => toggleRowExpand(order.order_id)}
                        >
                          <svg
                            className={`w-4 h-4 transition-transform ${
                              expandedRows.has(order.order_id)
                                ? "rotate-180"
                                : ""
                            }`}
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M19 9l-7 7-7-7"
                            />
                          </svg>
                        </button>
                      </div>
                      <span
                        className={`text-xs text-gray-500 md:block ${
                          expandedRows.has(order.order_id) ? "block" : "hidden"
                        }`}
                      >
                        {new Date(order.order_date).toLocaleDateString(
                          "en-US",
                          {
                            month: "short",
                            day: "numeric",
                            year: "numeric",
                          }
                        )}
                        {" • "}
                        {new Date(order.order_date).toLocaleTimeString(
                          "en-US",
                          {
                            hour: "numeric",
                            minute: "2-digit",
                          }
                        )}
                      </span>
                    </div>
                  </td>
                  <td className="p-3 md:p-4 text-xs md:text-sm font-medium text-gray-900">
                    {order.customer_name}
                  </td>
                  <td className="p-3 md:p-4 text-xs md:text-sm font-bold text-gray-900">
                    ${order.total_amount.toFixed(2)}
                  </td>
                  <td className="p-3 md:p-4">
                    <span
                      className={`px-3 py-1.5 rounded-full text-xs font-semibold whitespace-nowrap shadow-sm ${
                        order.status === "Completed"
                          ? "bg-gradient-to-r from-green-100 to-green-50 text-green-800 border border-green-200"
                          : order.status === "Cancelled"
                          ? "bg-gradient-to-r from-red-100 to-red-50 text-red-800 border border-red-200"
                          : order.status === "Ready"
                          ? "bg-gradient-to-r from-yellow-100 to-yellow-50 text-yellow-800 border border-yellow-200"
                          : "bg-gradient-to-r from-blue-100 to-blue-50 text-blue-800 border border-blue-200"
                      }`}
                    >
                      {order.status}
                    </span>
                  </td>
                  <td className="p-3 md:p-4">
                    {order.status === "Ready" &&
                      !selectedOrdersMap[order.order_id] && (
                        <button
                          onClick={() =>
                            handlePaymentChange(order.order_id, "Completed")
                          }
                          disabled={processingOrders.has(order.order_id)}
                          className={`px-4 py-2 text-xs md:text-sm rounded-lg font-semibold transition-all transform hover:scale-105 ${
                            processingOrders.has(order.order_id)
                              ? "bg-gray-400 cursor-not-allowed"
                              : "bg-gradient-to-r from-green-500 to-emerald-500 hover:from-green-600 hover:to-emerald-600 shadow-md hover:shadow-lg"
                          } text-white`}
                        >
                          {processingOrders.has(order.order_id)
                            ? "Processing..."
                            : "Mark as Paid"}
                        </button>
                      )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <Pagination />
        </div>
      </div>

      {Object.keys(selectedOrdersMap).length > 0 && (
        <div className="sticky bottom-6 left-0 right-0 mx-4 md:mx-6 p-4 md:p-6 bg-white/95 backdrop-blur-sm rounded-xl border border-gray-200/60 shadow-xl flex flex-col md:flex-row justify-between items-start md:items-center gap-4 z-50">
          <div>
            <p className="text-base md:text-lg font-semibold text-gray-800">
              Total Selected: <span className="text-green-600">${totalSelectedAmount.toFixed(2)}</span>
            </p>
            <p className="text-sm text-gray-600">
              {Object.keys(selectedOrdersMap).length} order(s) selected
            </p>
          </div>
          <button
            onClick={handleMarkSelectedPaid}
            disabled={isProcessingBulk}
            className={`w-full md:w-auto px-6 py-3 text-sm md:text-base rounded-lg font-semibold transition-all transform hover:scale-105 ${
              isProcessingBulk
                ? "bg-gray-400 cursor-not-allowed"
                : "bg-gradient-to-r from-green-500 to-emerald-500 hover:from-green-600 hover:to-emerald-600 shadow-md hover:shadow-lg"
            } text-white`}
          >
            {isProcessingBulk ? "Processing..." : "Mark Selected as Paid"}
          </button>
        </div>
      )}
    </div>
  );
};

export default OrderSummary;